import STBoardComments from '../../../assets/styles/organisms/boardComment.module.scss';
import STButton from '../../../assets/styles/atoms/button.module.scss';
import {
  ChangeEvent,
  useContext,
  useState,
  KeyboardEvent,
  useRef,
} from 'react';
import { postBoardComment } from '../../../utils/api/postComment';
import { useParams } from 'react-router-dom';
import { LoginUserContext } from '../../../providers/LoginUserProvider';
import type { TAppMember } from '../../../types/TAppMember';
import { countChars } from '../../../hooks/useBoardForm';
import { putBoardComment } from '../../../utils/api/putComment';
import type { UpdateCommentRequest } from '../../../api/board_comment';
import Avatar from '../../atoms/Avatar';
import { useBoard } from '../../../providers/BoardDetailProvider';
import {
  COMMENT_PUBLIC,
  FORUM_APPROVAL_SETTING,
} from '../../../utils/boardDetailConstants';
import { useGetTabUUID } from './hooks';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import EditNicknameAvatar from '../EditNicknameAvatar';
import AccountIcon from '../../atoms/icons/AccountIcon';
import { getDisplayNickname } from '../getDisplayNickname';
import MessageBoardNickname from '../../atoms/MessageBoardNickname';
import { SiteContext } from '../../../providers/SiteProvider';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { getSubdomain } from '../../../utils/getSubdomain';
import { useImageUploader } from '../../../hooks/useImageUploader';
import { MAX_COMMENT_IMAGES } from '../../../utils/constants/comment';

type ContentCommentFieldProps = {
  commentId?: number;
  parentId?: number;
  // 返信への返信（ACD-2699）: 返信先コメントの情報。親コメントへの直接返信では未指定
  replyToCommentId?: number;
  replyToUid?: string;
  replyToNickname?: string;
  defaultValue?: string;
  isEdit?: boolean;
  onToggleEditForm?: () => void;
  onCloseForm?: () => void;
  imageUrls?: [];
};

const MAX_LENGTH = 1000;

export const ContentCommentField = (props: ContentCommentFieldProps) => {
  const {
    commentId = 0,
    parentId = 0,
    replyToCommentId = 0,
    replyToUid = '',
    replyToNickname = '',
    defaultValue = '',
    isEdit = false,
    imageUrls,
    onCloseForm,
  } = props;
  const { board_id } = useParams<{ board_id: string }>();
  const {
    addComment,
    addReplyComment,
    editComment,
    editReplyComment,
    deleteComment,
    deleteReplyComment,
    updateShowPendingApprovalCommentPage,
    updateSnackbar,
  } = useBoard();
  const deviceId = useGetTabUUID();
  const currentUser = useContext(LoginUserContext);
  const { member = {}, loginUser } = currentUser ?? {};
  const { nickname = '', avatar_url = '', name = '' } = member as TAppMember;
  const { uid } = loginUser ?? {};
  const site = useContext(SiteContext);
  const { isAutoBoardApproval = 0 } = site ?? {};
  const textSubmitButton =
    isAutoBoardApproval === FORUM_APPROVAL_SETTING.APPROVAL_ALL
      ? '申請する'
      : '投稿する';

  const subdomain = getSubdomain(window.location.hostname);
  const { uploadImage } = useImageUploader(subdomain);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedImages, setSelectedImages] = useState<(File | string)[]>(
    imageUrls || [],
  );
  const uploadRef = useRef<HTMLInputElement>(null);

  const [text, setText] = useState<string>(defaultValue || '');
  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event?.target?.value || '';
    if (countChars(value) >= MAX_LENGTH) {
      setErrorMessage(`${MAX_LENGTH / 2}字以内で入力してください`);
    } else {
      setErrorMessage('');
    }
    setText(value);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    const updatedList = [...selectedImages, ...newFiles];
    const limitedList = updatedList.slice(-MAX_COMMENT_IMAGES);

    setSelectedImages(limitedList);

    if (e.target) e.target.value = '';
  };

  const removeImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreateNewComment = async () => {
    if (!board_id || (!text && selectedImages.length === 0) || !!errorMessage) {
      return;
    }
    setIsLoading(true);
    try {
      let imageUrls: string[] = [];
      if (selectedImages.length !== 0) {
        imageUrls = await Promise.all(selectedImages.map(uploadImage));
      }

      const postBody = {
        content: text,
        board_id: Number(board_id),
        parent_id: parentId ? Number(parentId) : 0,
        ...(replyToCommentId
          ? { reply_to_comment_id: Number(replyToCommentId) }
          : {}),
        device_id: deviceId,
        image_urls: imageUrls,
      };

      if (!isEdit) {
        await postBoardComment(postBody).then((response) => {
          const { isSuccess, result = {} } = response;
          if (!isSuccess) {
            throw new Error('');
          } else {
            setText('');
            setSelectedImages([]);
            const response = result?.data;
            const isWaitedApprovedOrHasNGWords =
              response?.[0].status !== COMMENT_PUBLIC;
            if (response) {
              if (isWaitedApprovedOrHasNGWords) {
                updateShowPendingApprovalCommentPage(true);
              } else {
                const newComment = {
                  ...response[0],
                  parent_id: parentId,
                  reply_to_comment_id: replyToCommentId || null,
                  reply_to_uid: replyToCommentId ? replyToUid : null,
                  reply_to_nickname: replyToCommentId ? replyToNickname : null,
                  nickname,
                  name,
                  avatar_url,
                  total_reply_comments: 0,
                  total_of_likes: 0,
                  like_flag: 0,
                  showNow: true,
                  children: [],
                  comment_flag: 1,
                };
                if (!parentId) {
                  addComment(newComment);
                } else {
                  addReplyComment(parentId, newComment, 'api');
                }
                updateSnackbar({
                  message: 'コメントが完了しました！',
                  severity: 'success',
                });
              }
            }
            if (onCloseForm) onCloseForm();
          }
        });
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateComment = async () => {
    if (!board_id || (!text && selectedImages.length === 0) || !!errorMessage) {
      return;
    }
    setIsLoading(true);
    try {
      let imageUrls: string[] = [];
      if (selectedImages.length !== 0) {
        imageUrls = await Promise.all(selectedImages.map(uploadImage));
      }

      const params: UpdateCommentRequest = {
        content: text,
        board_id: Number(board_id),
        comment_id: Number(commentId),
        device_id: deviceId,
        image_urls: imageUrls,
      };

      if (parentId) {
        params.parent_id = Number(parentId);
      }

      await putBoardComment(params).then((response: any) => {
        const { isSuccess, result = {} } = response;
        if (!isSuccess) {
          throw new Error('');
        } else {
          const newComment = result?.data;
          const isWaitedApprovedOrHasNGWords =
            newComment?.status !== COMMENT_PUBLIC;
          if (newComment) {
            const commentFlags = newComment?.comment_flags ?? {};
            let checkCommentFlag = 0;
            if (uid && Object.hasOwn(commentFlags, uid)) {
              checkCommentFlag = commentFlags[uid];
            }
            if (isWaitedApprovedOrHasNGWords) {
              updateShowPendingApprovalCommentPage(true);
              if (!parentId) {
                const countDeletedItems =
                  newComment.count_comments_waiting_approve_or_has_ng_words;
                deleteComment(commentId, countDeletedItems, checkCommentFlag);
              } else {
                deleteReplyComment(parentId, commentId, checkCommentFlag);
              }
            } else {
              if (!parentId) {
                editComment(commentId, { ...newComment, content: text });
              } else {
                editReplyComment(parentId, commentId, {
                  ...newComment,
                  content: text,
                });
              }
              updateSnackbar({
                message: 'コメントが完了しました！',
                severity: 'success',
              });
            }
          }
          setText('');
          setSelectedImages([]);
          if (onCloseForm) onCloseForm();
        }
      });
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const onBack = () => {
    setText('');
    setSelectedImages([]);
    onCloseForm && onCloseForm();
  };

  const onSubmit = async () => {
    if (isEdit) {
      await handleUpdateComment();
    } else {
      await handleCreateNewComment();
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (
      event.key === 'Enter' &&
      event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      event.stopPropagation();
      onSubmit();
    }
  };

  return (
    <div className={STBoardComments.formCreateParentComment}>
      <div className={STBoardComments.formInput}>
        {!isEdit && !!replyToCommentId && !!replyToNickname && (
          <p className={STBoardComments.replyToLabel}>
            <MessageBoardNickname
              component="span"
              uid={replyToUid}
              nickname={replyToNickname}
            />
            さんに返信
          </p>
        )}
        {selectedImages.length > 0 && (
          <div className={STBoardComments.previewImage}>
            {selectedImages.map((file, index) => {
              const src =
                typeof file === 'string' ? file : URL.createObjectURL(file);
              return (
                <div key={index} className={`${STBoardComments.wrapperImage}`}>
                  <img
                    src={src}
                    alt={`comment-upload-${index}`}
                    className={`${STBoardComments.uploadItem} forum-board-image`}
                  />
                  <button
                    onClick={() => removeImage(index)}
                    className={STBoardComments.removeImageButton}
                  >
                    <CloseIcon className={STBoardComments.removeIcon} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <textarea
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          className={`${STBoardComments.inputCreateParentComment} ${
            errorMessage ? STBoardComments.formError : ''
          }`}
        />
        {errorMessage && (
          <span
            className={`${
              !parentId ? STBoardComments.parentCommentErrorMessage : ''
            } ${STBoardComments.errorMessage}`}
          >
            {errorMessage}
          </span>
        )}
      </div>
      <div className={STBoardComments.commentActions}>
        <div className={STBoardComments.commentUpload}>
          <p className={STBoardComments.commentUploadDescription}>
            ※画像を選択（３枚まで）
          </p>
          <input
            accept="image/*"
            multiple
            type="file"
            ref={uploadRef}
            onChange={handleImageChange}
            style={{ display: 'none' }}
          />
          <button
            onClick={(e) => {
              e.preventDefault();
              uploadRef.current?.click();
            }}
            disabled={selectedImages.length >= MAX_COMMENT_IMAGES}
            style={{
              opacity: selectedImages.length >= MAX_COMMENT_IMAGES ? 0.5 : 1,
            }}
          >
            <CloudUploadIcon className={STBoardComments.uploadIcon} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {isEdit && (
            <button
              className={`${STBoardComments.backButton} ${STButton.secondary}`}
              onClick={onBack}
            >
              戻る
            </button>
          )}
          <button
            disabled={isLoading || (!text && selectedImages.length === 0)}
            style={{ opacity: errorMessage || isLoading ? 0.5 : 1 }}
            className={`${STBoardComments.buttonSubmitCreateParentComment} ${STButton.primary}`}
            onClick={onSubmit}
          >
            {textSubmitButton}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateNewComment = () => {
  const currentUser = useContext(LoginUserContext);
  const { setMember } = useContext(LoginUserContext);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState<boolean>(false);
  const onOpenEditDialog = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpenEditDialog(true);
  };
  const onEditDialogClose = () => {
    setIsOpenEditDialog(false);
  };

  return (
    <div className={STBoardComments.createComment}>
      <div>
        <div className={STBoardComments.userInforCommentRow}>
          <div className={STBoardComments.userInforCommentColumn}>
            {currentUser.member?.avatar_url ? (
              <Avatar
                avatarUrl={currentUser.member?.avatar_url}
                className={STBoardComments.userInforAvatar}
              />
            ) : (
              <div className={STBoardComments.userInforAvatar}>
                <AccountIcon />
              </div>
            )}
            <div
              className={STBoardComments.editAvatar}
              onClick={onOpenEditDialog}
            >
              <MessageBoardNickname
                nickname={getDisplayNickname(
                  currentUser.member?.nickname,
                  currentUser.member?.name,
                )}
                className={STBoardComments.editAvatarTextTitle}
              />
              <div>
                <EditOutlinedIcon
                  style={{ cursor: 'pointer' }}
                  fontSize="inherit"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContentCommentField />

      {isOpenEditDialog && (
        <EditNicknameAvatar
          nickname={currentUser.member?.nickname ?? ''}
          avatar_url={currentUser.member?.avatar_url ?? ''}
          onClose={onEditDialogClose}
          onSuccess={({ nickname, avatar_url, name, avatarType }) => {
            setMember((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                nickname,
                avatar_url,
                name,
                avatarType,
              };
            });
          }}
        />
      )}
    </div>
  );
};

export default CreateNewComment;
